import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { createTemplateFolder } from '../../../api/mutations';
import { getTemplateFolders } from '../../../api/queries_v2';
import { FolderTreeSelector } from '../../../components/folder-tree-selector';
import PropdocsModal from '../../../components/modal/PropdocsModal';

interface CreateFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  parentFolderId?: string;
  onFolderCreated: () => void;
}

export const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  isOpen,
  onClose,
  teamId,
  parentFolderId,
  onFolderCreated,
}) => {
  const { formatMessage } = useIntl();
  const [folderName, setFolderName] = useState('');
  const [selectedParentFolderId, setSelectedParentFolderId] = useState<string | null>(parentFolderId || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch all template folders for the tree selector
  const { data: foldersData, loading: foldersLoading } = useQuery(getTemplateFolders as any, {
    variables: { teamId, folderType: 'template' },
    skip: !isOpen,
  });

  const [createFolder] = useMutation(createTemplateFolder as any, {
    onCompleted: () => {
      onFolderCreated();
      handleClose();
    },
    onError: (error) => {
      console.error('Error creating folder:', error);
      setIsSubmitting(false);

      // Show generic error message - folder names should be allowed to duplicate
      setErrorMessage(error.message || 'Failed to create folder. Please try again.');
    },
  });

  const handleClose = () => {
    setFolderName('');
    setSelectedParentFolderId(parentFolderId || null);
    setIsSubmitting(false);
    setErrorMessage(null);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folderName.trim()) return;

    setIsSubmitting(true);
    setErrorMessage(null); // Clear previous errors

    try {
      await createFolder({
        variables: {
          name: folderName.trim(),
          teamId,
          parentFolderId: selectedParentFolderId,
          folderType: 'template',
        },
      });
    } catch (error) {
      // Error handling is done in the onError callback
      setIsSubmitting(false);
    }
  };

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="md"
      disableClose={isSubmitting}
      title={<FormattedMessage id="templates.folders.create" defaultMessage="Create New Folder" />}
    >
      <>
        <div>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="folderName" className="form-label">
                <FormattedMessage id="templates.folders.name" defaultMessage="Folder Name" />
              </label>
              <input
                type="text"
                id="folderName"
                className={`form-control ${errorMessage ? 'is-invalid' : ''}`}
                value={folderName}
                onChange={(e) => {
                  setFolderName(e.target.value);
                  if (errorMessage) setErrorMessage(null); // Clear error when user types
                }}
                placeholder={formatMessage({
                  id: 'templates.folders.name.placeholder',
                  defaultMessage: 'Enter folder name',
                })}
                required
                autoFocus
              />
              {errorMessage && <div className="invalid-feedback d-block">{errorMessage}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">
                <FormattedMessage id="templates.folders.parentFolder" defaultMessage="Parent Folder" />
              </label>
              {foldersLoading ? (
                <div className="text-muted small">
                  <FormattedMessage id="templates.folders.loading" defaultMessage="Loading folders..." />
                </div>
              ) : (
                <FolderTreeSelector
                  folders={foldersData?.getTemplateFolders || []}
                  selectedFolderId={selectedParentFolderId}
                  onSelectionChange={setSelectedParentFolderId}
                  disabled={isSubmitting}
                  context="templates"
                />
              )}
            </div>
          </form>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!folderName.trim() || isSubmitting}>
            {isSubmitting ? (
              <FormattedMessage id="common.creating" defaultMessage="Creating..." />
            ) : (
              <FormattedMessage id="templates.folders.create" defaultMessage="Create Folder" />
            )}
          </Button>
        </div>
      </>
    </PropdocsModal>
  );
};
